/**
 * Handlers for various closure scenarios (early close, completely closed, consecutive closures)
 */
import { toast } from 'react-toastify';
import dateHelper from '@/components/QuoteForm/dateHelper';
import {
    ClosedDateDetails,
    ClosurePeriodInfo,
    ConsecutiveClosureInfo,
} from '../types';
import {
    BUSINESS_RULES,
    CLOSURE_TYPES,
    LOCALE_DATE_FORMAT,
} from '../constants';
import { toBusinessDate, fromBusinessDate } from '@/utils/date/businessDate';

/**
 * Find closure period information for a given return date
 */
export const findClosurePeriod = (
    returnDate: Date,
    closedDatesDetails: ClosedDateDetails[]
): ClosurePeriodInfo => {
    const returnBusinessDate = toBusinessDate(returnDate);

    // Check if return date is an early close day
    const earlyCloseDate = closedDatesDetails.find(
        (item) =>
            item.date === returnBusinessDate &&
            item.closingTime !== CLOSURE_TYPES.CLOSED_ALL_DAY
    );

    // Check if return date is a completely closed day
    const completelyClosedDate = closedDatesDetails.find(
        (item) =>
            item.date === returnBusinessDate &&
            item.closingTime === CLOSURE_TYPES.CLOSED_ALL_DAY
    );

    // Check if return date is a Sunday
    const isSunday = returnDate.getDay() === 0;

    return {
        isEarlyCloseDay: !!earlyCloseDate,
        isCompletelyClosedDay: !!completelyClosedDate,
        isSunday,
        earlyCloseDetails: earlyCloseDate,
        isWithinClosurePeriod: !!(
            earlyCloseDate ||
            completelyClosedDate ||
            isSunday
        ),
    };
};

/**
 * Find the end of consecutive closure period
 */
export const findConsecutiveClosureEnd = (
    startDate: Date,
    closedDatesDetails: ClosedDateDetails[]
): ConsecutiveClosureInfo => {
    const currentDate = new Date(startDate);
    let lastClosedDate = new Date(startDate);
    let reopenDate: string | null = null;
    let daysChecked = 0;

    // Keep checking consecutive days until we find an open day
    while (daysChecked < BUSINESS_RULES.MAX_CONSECUTIVE_DAYS_CHECK) {
        const currentBusinessDate = toBusinessDate(currentDate);
        const closureInfo = findClosurePeriod(currentDate, closedDatesDetails);

        if (closureInfo.isWithinClosurePeriod) {
            lastClosedDate = new Date(currentDate);
            // If this is an early close or completely closed day with a specific reopen date, use that
            const closedDateEntry = closedDatesDetails.find(
                (item) => item.date === currentBusinessDate
            );
            if (closedDateEntry && closedDateEntry.reopenDate) {
                reopenDate = closedDateEntry.reopenDate;
            }
        } else {
            // Found an open day, so the closure period ends
            break;
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
        daysChecked++;
    }

    // If we found a specific reopen date, use that; otherwise use the day after last closed date
    const finalReopenDate = reopenDate
        ? fromBusinessDate(reopenDate)
        : new Date(lastClosedDate.getTime() + 24 * 60 * 60 * 1000);

    const dayBeforeReopen = new Date(finalReopenDate);
    dayBeforeReopen.setDate(dayBeforeReopen.getDate() - 1);
    dayBeforeReopen.setHours(17, 0, 0, 0); // Set to 5pm the day before reopen

    return {
        lastClosedDate,
        reopenDate: finalReopenDate,
        dayBeforeReopen,
        isConsecutiveClosurePeriod:
            lastClosedDate.getTime() > startDate.getTime(),
    };
};

/**
 * Handle early close day logic
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const handleEarlyCloseDay = (
    currentEarlyCloseDate: ClosedDateDetails,
    retDate: Date,
    hours: number,
    minutes: number,
    returnDatePriceCalculator: any,
    setReturnDatePriceCalculator: any,
    mySetFieldValue: any,
    toastify: any,
    numRentalDays?: number
): boolean => {
    const returnDateOnly = new Date(
        retDate.getFullYear(),
        retDate.getMonth(),
        retDate.getDate()
    );
    const earlyCloseDateOnly = fromBusinessDate(currentEarlyCloseDate.date);
    // Guard: derive reopen date — use explicit CMS value or fall back to next calendar day
    const resolveReopenDate = (entry: ClosedDateDetails): Date => {
        if (entry.reopenDate) return fromBusinessDate(entry.reopenDate);
        const nextDay = fromBusinessDate(entry.date);
        nextDay.setDate(nextDay.getDate() + 1);
        return nextDay;
    };
    const reopenDateOnly = fromBusinessDate(currentEarlyCloseDate.reopenDate);

    // Check if this is a Sunday/Monday return within the early close period
    const isWithinClosurePeriod =
        returnDateOnly > earlyCloseDateOnly && returnDateOnly < reopenDateOnly;

    if (isWithinClosurePeriod) {
        // Apply the same 4-day logic regardless of time
        // ... same calculation as Saturday
        return true;
    }

    const [closeHour, closeMinute] = currentEarlyCloseDate.closingTime
        .split(':')
        .map((v) => parseInt(v.replace(/[^\d]/g, ''), 10));

    const closeTimeInMinutes = closeHour * 60 + closeMinute;
    const returnTimeInMinutes = hours * 60 + minutes;
    const oneHourBeforeClose =
        closeTimeInMinutes - BUSINESS_RULES.EARLY_CLOSE_WARNING_MINUTES;

    // If returning within 1 hour of early close time
    if (returnTimeInMinutes > oneHourBeforeClose) {
        // For early close days, use the specific reopenDate from the CMS
        const reopenDate = resolveReopenDate(currentEarlyCloseDate);

        const dayBeforeReopen = new Date(reopenDate);
        dayBeforeReopen.setDate(dayBeforeReopen.getDate() - 1);

        // Calculate days from RETURN date to day before reopen
        const daysUntilReopen = Math.ceil(
            (dayBeforeReopen.getTime() - retDate.getTime()) /
                (1000 * 60 * 60 * 24)
        );

        // Close any open toast
        toast.dismiss();

        dateHelper.fixDateAndTime({
            returnDatePriceCalculator,
            setReturnDatePriceCalculator,
            mySetFieldValue,
            toastify,
            numDays: daysUntilReopen,
            numRentalDays,
            isEarlyClose: true,
            message: `We are closed or closing on the day of return, you are welcome to drop the keys but the rental is calculated to ${dayBeforeReopen.toLocaleDateString(LOCALE_DATE_FORMAT)} at 5pm (which is the night before we re-open). We are closing at ${currentEarlyCloseDate.closingTime} on ${new Date(currentEarlyCloseDate.date).toLocaleDateString(LOCALE_DATE_FORMAT)}, any vehicles would need to be returned at least an hour before we close to avoid charging until we re-open ${currentEarlyCloseDate.reopenDate ? ` on ${new Date(currentEarlyCloseDate.reopenDate).toLocaleDateString(LOCALE_DATE_FORMAT)}.` : `.`}`,
        });

        return true; // Handled - exit early
    }

    return false; // Not handled - continue with normal logic
};

/**
 * Handle completely closed day logic
 */
export const handleCompletelyClosedDay = (
    retDate: Date,
    closedDatesDetails: ClosedDateDetails[],
    returnDatePriceCalculator: any,
    setReturnDatePriceCalculator: any,
    mySetFieldValue: any,
    toastify: any,
    numRentalDays?: number,
    collectReturnSaturday?: boolean,
    isEarlyClose?: boolean
): boolean => {
    const consecutiveClosureInfo = findConsecutiveClosureEnd(
        retDate,
        closedDatesDetails
    );

    // Calculate days from RETURN date to day before reopen, plus 1 for the charging period
    const daysUntilReopen = Math.max(
        0,
        Math.ceil(
            (consecutiveClosureInfo.dayBeforeReopen.getTime() -
                retDate.getTime()) /
                (1000 * 60 * 60 * 24)
        ) + 1
    );

    // Close any open toast
    toast.dismiss();

    dateHelper.fixDateAndTime({
        returnDatePriceCalculator,
        setReturnDatePriceCalculator,
        mySetFieldValue,
        toastify,
        numDays: daysUntilReopen,
        message: `We are closed on the day of return, you are welcome to drop the keys but the rental is calculated to ${consecutiveClosureInfo.dayBeforeReopen.toLocaleDateString(LOCALE_DATE_FORMAT)} at 5pm (which is the night before we re-open). We re-open on ${consecutiveClosureInfo.reopenDate.toLocaleDateString(LOCALE_DATE_FORMAT)}.`,
        numRentalDays,
        collectReturnSaturday,
        isEarlyClose,
    });

    return true; // Handled - exit early
};

/**
 * Handle consecutive closure period logic
 */
export const handleConsecutiveClosurePeriod = (
    closurePeriodInfo: ClosurePeriodInfo,
    currentEarlyCloseDate: ClosedDateDetails | undefined,
    retDate: Date,
    closedDatesDetails: ClosedDateDetails[],
    returnDatePriceCalculator: any,
    setReturnDatePriceCalculator: any,
    mySetFieldValue: any,
    toastify: any,
    numRentalDays?: number,
    collectReturnSaturday?: boolean,
    isEarlyClose?: boolean
): boolean => {
    // Handle any return within a consecutive closure period
    if (
        closurePeriodInfo.isWithinClosurePeriod &&
        !currentEarlyCloseDate &&
        !closurePeriodInfo.isCompletelyClosedDay
    ) {
        // This catches bank holidays, Sundays, or any other closure days during a consecutive period
        const consecutiveClosureInfo = findConsecutiveClosureEnd(
            retDate,
            closedDatesDetails
        );

        // Only apply consecutive closure logic if:
        // 1. There actually IS a consecutive closure period, AND
        // 2. The closure period involves CMS-defined closed dates (not just Sunday + bank holiday)
        const hasCMSClosedDates = closedDatesDetails.some((closedDate) => {
            const closedDateTime = new Date(closedDate.date);
            return (
                closedDateTime >= retDate &&
                closedDateTime <= consecutiveClosureInfo.lastClosedDate
            );
        });

        if (
            consecutiveClosureInfo.isConsecutiveClosurePeriod &&
            hasCMSClosedDates
        ) {
            // Calculate days from RETURN date to day before reopen, plus 1 for the charging period
            const daysUntilReopen =
                Math.ceil(
                    (consecutiveClosureInfo.dayBeforeReopen.getTime() -
                        retDate.getTime()) /
                        (1000 * 60 * 60 * 24)
                ) + 1;

            // Close any open toast
            toast.dismiss();

            dateHelper.fixDateAndTime({
                returnDatePriceCalculator,
                setReturnDatePriceCalculator,
                mySetFieldValue,
                toastify,
                numDays: daysUntilReopen,
                message: `We are closed on the day of return, you are welcome to drop the keys but the rental is calculated to ${consecutiveClosureInfo.dayBeforeReopen.toLocaleDateString(LOCALE_DATE_FORMAT)} at 5pm (which is the night before we re-open). We re-open on ${consecutiveClosureInfo.reopenDate.toLocaleDateString(LOCALE_DATE_FORMAT)}.`,
                numRentalDays,
                collectReturnSaturday,
                isEarlyClose,
            });

            return true; // Handled - exit early
        }
    }

    return false; // Not handled - continue with normal logic
};
